<script setup lang="ts">
import {
  buildUrlPrefix,
  getProductRoute,
  getTranslatedProperty,
} from "@shopware/helpers";
import type { Ref } from "vue";
import type { Schemas } from "#shopware";
import ProductImageGallery from "~/components/cms/ProductImageGallery.vue";
import {useNotifications, useProductWishlist, useUrlResolver} from "#imports";
import { ApiClientError } from "@shopware/api-client";
import {defu} from "defu";
import {useCmsTranslations} from "@shopware/composables";
import {getProductBundles} from "~/components/utilities";
import StickyProductAddToCart from "~/components/product/StickyProductAddToCart.vue";
import BundleProductLineItem from "~/components/product/BundleProductLineItem.vue";
import {productDetailTracking} from "~/components/utilities/GtmTrackings";
import ProductsSocialMedia from "~/components/product/ProductsSocialMedia.vue";
const { trackProductView, trackAddToWishlist } = useSellograph();
const { pushSuccess, pushError, pushWarning } = useNotifications();
const { getUrlPrefix } = useUrlResolver();
const { t } = useI18n();
const props = defineProps<{
  product: Schemas["Product"];
}>();
const reviews: Ref<Schemas["ProductReview"][]> = ref([]);
const router = useRouter();

const { apiClient } = useShopwareContext();

const productName = computed(() =>
  getTranslatedProperty(props.product, "name"),
);
const manufacturerName = computed(() =>
  getTranslatedProperty(props.product.manufacturer, "name"),
);

const description = computed(() =>
  getTranslatedProperty(props.product, "description"),
);
const properties = computed(() => props.product?.properties || []);

const handleVariantChange = (val: Schemas["Product"]) => {
  const newRoute = getProductRoute(val);
  router.push(newRoute);
};

const Bundles  = await getProductBundles(props.product);

let medias = [];
if (props.product && props.product.media) {
  props.product.media.forEach(media => {
    medias.push(media);
  });
}

let additionalMedia = [];
if (Bundles) {
  Bundles.forEach(product => {
    if (product.cover && product.cover.media) {
      additionalMedia.push(product.cover);
    }
  })
}

const discount = Math.floor(<number>props.product.calculatedPrice?.listPrice?.percentage) || null

type Translations = {
  product: {
    addedToWishlist: string;
    reason: string;
    cannotAddToWishlist: string;
  };
};

let translations: Translations = {
  product: {
    addedToWishlist: "has been added to wishlist.",
    reason: "Reason",
    cannotAddToWishlist: "cannot be added to wishlist.",
  },
};

translations = defu(translations, useCmsTranslations()) as Translations;

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
    props.product.id,
);

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      trackAddToWishlist({
        currency: 'CHF',
        items: [{ item_id: props.product.productNumber, item_name: getTranslatedProperty(props.product, 'name'), price: props.product.calculatedPrice?.totalPrice }],
      });
      return pushSuccess(`<b>${props.product?.translated?.name}</b>` + t("product.messages.addedToWishlist"));
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
            ? `${translations.product.reason}: ${error.details.errors?.[0]?.detail}`
            : "";
        return pushError(
            `<b>${props.product?.translated?.name}</b>` + t("product.messages.annotAddToWishlist") + `\n${reason}`,
            {
              timeout: 5000,
            },
        );
      }
    }
  }
  pushWarning(
      `<b>${props.product?.translated?.name}</b> ` + t("product.messages.removedFromWishlist"),
      { timeout: 5000}
  );
  removeFromWishlist();
};

onMounted(async () => {
  /*const reviewsResponse = await apiClient.invoke(
      "readProductReviews post /product/{productId}/reviews",
      {
        pathParams: {
          productId: props.product.id,
        }
      },
  );*/
  reviews.value = []; //reviewsResponse?.elements || [];

  if (document) {
    // Get all tab buttons
    const tabButtons = document.querySelectorAll('.nav-link');

    // Get all tab content divs
    const tabContents = document.querySelectorAll('.tab-pane');

    // Add click event listener to each tab button
    tabButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        // Prevent default link behavior
        e.preventDefault();

        // Get the target tab content id from the button's data-bs-target attribute
        const targetId:string | null = button.getAttribute('data-bs-target');

        // Hide all tab content
        tabContents.forEach((tabContent) => {
          tabContent.classList.remove('active');
          tabContent.classList.remove('show');
        });

        // Show the target tab content
        const targetTabContent = document.querySelector(targetId);
        targetTabContent?.classList.add('active');
        targetTabContent?.classList.add('show');

        // Remove 'active' class from all tab buttons
        tabButtons.forEach((tabButton) => {
          tabButton.classList.remove('active');
        });

        // Add 'active' class to the clicked tab button
        button.classList.add('active');
      });
    });
  }

  productDetailTracking(props.product);

  trackProductView({
    currency: 'CHF',
    value: props.product.calculatedPrice?.totalPrice,
    items: [{
      item_id: props.product.productNumber,
      item_name: getTranslatedProperty(props.product, 'name'),
      item_brand: getTranslatedProperty(props.product.manufacturer, 'name'),
      price: props.product.calculatedPrice?.totalPrice,
      currency: 'CHF',
      quantity: 1,
    }],
  });
});

</script>

<template>
  <div class="container">
    <div class="section__body">
      <div class="product">
        <div class="product__body">
          <etrusted-widget data-etrusted-widget-id="wdg-51651cc8-579d-4d1c-a309-adbbc381420b"></etrusted-widget>

          <div class="row">
            <div class="col-md-6">
              <ProductImageGallery :media="medias" :additionalMedia="additionalMedia" />
              <div class="product__image">
                <div class="product__image-inner">
                  <ul class="list-badges product__list-badges">
                    <li v-if="discount">
                      <span class="badge badge--large bg-danger--light">{{ discount }}%</span>
                    </li>
                  </ul><!-- /.list-badges -->
                  <a aria-label="Add to wishlist"
                     href="javascript:;"
                     @click="toggleWishlistProduct"
                     class="btn-danger btn-round btn-round--large"
                     :class="{'wishlist-added': isInWishlist}"
                     data-testid="product-box-toggle-wishlist-button">
                    <client-only>
                      <i v-if="isInWishlist" class="ico-heart added">
                        <img src="assets/images/svg/heart.svg" alt="" width="21" height="20">
                      </i>
                      <i v-else class="ico-heart">
                        <img src="assets/images/svg/heart.svg" alt="" width="21" height="20">
                      </i>
                      <template #placeholder>
                        <i class="ico-heart">
                          <img src="assets/images/svg/heart.svg" alt="" width="21" height="20">
                        </i>
                      </template>
                    </client-only>
                  </a>

                </div><!-- /.product__image-inner -->

              </div>
            </div>

            <div class="col-md-6">

              <div class="product__content">
                <h2 v-html="productName" />
                <p class="product-claim">{{ $t('product.main.claim')}}</p>
                <div class="product-tipps">
                    <div class="info--primary product__info--primary position-relative product_facts">
                      <i class="ico-time-solid">&#x1f973;<img src="assets/icons/heart.svg" alt="" width="30" height="30"></i>
                      <span>{{ $t('product.already.filled') }}</span>
                    </div>

                    <div class="info--primary product__info--primary position-relative product_facts">
                      <i class="ico-time-solid">&#x1f973;<img src="assets/icons/distance.svg" alt="" width="30" height="30"></i>
                      <span>{{ $t('product.over.distance') }}</span>
                    </div>

                    <div class="info--primary product__info--primary position-relative product_facts">
                      <i class="ico-time-solid">&#x1f973;<img src="assets/icons/happy.svg" alt="" width="30" height="30"></i>
                      <span>{{ $t('product.emotional.message') }}</span>
                    </div>

                    <div class="info--primary product__info--primary position-relative garantie">
                      <i class="ico-time-solid">&#x1f973;<img src="assets/icons/white_stars.svg" alt="" width="18" height="18"></i>

                      <span>{{ $t('product.garantie.headline') }}</span>
                      <a class="ms-2 tool-link" style="color:#fff;" href="javascript:;"><img src="assets/icons/question.svg" alt="" width="18" height="18">
                        <span class="guarantee-tip">{{ $t('product.garantie.text') }}</span>
                      </a>
                    </div>
                </div>

                <ProductPrice :product="product" />
                <ProductUnits :product="product" class="text-sm" />

                <p class="product-detail-tax"><span>{{$t('product.price.info')}}</span></p>

                <div class="info--primary product__info--primary">
                  <i class="ico-time-solid">
                    <img src="assets/icons/clock.svg" alt="" width="27" height="27">
                  </i>

                  <span>{{ $t('product.delivery.promise') }}</span>
                </div><!-- /.info-/-primary -->

                <ClientOnly>
                <div class="mb-4" v-if="Bundles && Bundles?.length > 0">
                  <b class="d-block mb-4">In diesem Bundle enthaltene Produkte:</b>
                  <div class="bundle-box">
                    <template v-for="(product, index) in Bundles">
                      <BundleProductLineItem :product="product"></BundleProductLineItem>
                    </template>
                  </div>
                  <template v-for="(product, index) in Bundles">
                  <router-link :to="buildUrlPrefix(getProductRoute(product), getUrlPrefix())" target="_blank" class="mb-2">
                    {{ product.name }}
                  </router-link><br>
                  </template>
                </div>
                </ClientOnly>

                <div class="product__actions">
                  <p class="text-primary my-4" v-if="product.availableStock && product.availableStock <= 5 && product.availableStock >= 1"> {{ $t('product.only_left.prefix') }} {{ product.availableStock }} {{ $t('product.only_left.suffix') }}</p>
                  <p class="text-primary my-4" v-if="product.availableStock && product.availableStock >= 6 && product.availableStock <= 1000"> {{ $t('product.only_left.a.few') }}</p>

                  <ProductAddToCart v-if="!Bundles || Bundles.length < 1" :product="product" />
                  <ProductAddBundleToCart v-else :products="Bundles" />
                  <etrusted-widget data-etrusted-widget-id="wdg-51651cc8-579d-4d1c-a309-adbbc381420b"></etrusted-widget>
                  <StickyProductAddToCart :isBundle="Bundles" :products="(Bundles ? Bundles : null)" :product="product"></StickyProductAddToCart>

                </div><!-- /.product__actions -->

                <div class="product-payment-info">
                  <img src="assets/icons/rechnung.svg">
                  <img src="assets/icons/visa.svg">
                  <img src="assets/icons/postfinance.svg">
                  <img src="assets/icons/mastercard.svg">
                  <img src="assets/icons/twint.svg">
                  <img src="assets/icons/paypal.svg">
                </div>
              </div><!-- /.product__content -->
            </div>
          </div>
        </div><!-- /.product__body -->

        <div class="product__foot ">
          <div class="text-center">
           <img src="assets/icons/customerbubble.svg"> <span>{{ $t('product.marketing.claim') }}</span>
          </div>

          <div class="text-center mt-4">

            <div class="tv-shows-headline">Bekannt aus:</div>
            <div class="tv-shows">
              <img src="assets/icons/sat1.svg">
              <img src="assets/icons/pro7.svg">
              <img src="assets/icons/dmax.svg">
              <img src="assets/icons/kabel_eins.svg">
            </div>
          </div>
        </div><!-- /.product__foot -->
      </div><!-- /.product -->
    </div><!-- /.section__body -->
  </div>

  <section class="section section-steps section-steps--alt">
    <div class="container">
      <ul class="steps--alt steps--secondary section__steps--alt">
        <li class="step--alt step--secondary">
          <div class="step__inner">
            <div class="step__count">
              <img src="assets/images/svg/1.svg" alt="" width="20" height="56">
            </div><!-- /.step__icon -->

            <div class="step__icon">
              <i class="ico-balloon">
                <img src="assets/images/svg/balloons.svg" alt="" width="76" height="70">
              </i>
            </div><!-- /.step__count -->
          </div><!-- /.step__inner -->

          <div class="step__content">
            <p v-html="$t('product.step_1')" />
          </div><!-- /.step__inner -->
        </li><!-- /.step -->

        <li class="step--alt step--secondary">
          <div class="step__inner">
            <div class="step__count">
              <img src="assets/images/svg/2.svg" alt="" width="30" height="56">
            </div><!-- /.step__count -->

            <div class="step__icon">
              <i class="ico-balloon">
                <img src="assets/images/svg/candy.svg" alt="" width="65" height="56">
              </i>
            </div><!-- /.step__icon -->
          </div><!-- /.step__inner -->

          <div class="step__content">
            <p v-html="$t('product.step_2')" />
          </div><!-- /.step__inner -->
        </li><!-- /.step -->

        <li class="step--alt step--secondary">
          <div class="step__inner">
            <div class="step__count">
              <img src="assets/images/svg/3.svg" alt="" width="30" height="56">
            </div><!-- /.step__icon -->

            <div class="step__icon">
              <i class="ico-balloon">
                <img src="assets/images/svg/gift-cart.svg" alt="" width="66" height="49">
              </i>
            </div><!-- /.step__count -->
          </div><!-- /.step__inner -->

          <div class="step__content">
            <p v-html="$t('product.step_3')" />
          </div><!-- /.step__inner -->
        </li><!-- /.step -->

        <li class="step--alt step--secondary">
          <div class="step__inner">
            <div class="step__count">
              <img src="assets/images/svg/4.svg" alt="" width="32" height="56">
            </div><!-- /.step__count -->

            <div class="step__icon">
              <i class="ico-balloon">
                <img src="assets/images/svg/notes.svg" alt="" width="58" height="61">
              </i>
            </div><!-- /.step__icon -->
          </div><!-- /.step__inner -->

          <div class="step__content">
            <p v-html="$t('product.step_4')" />
          </div><!-- /.step__inner -->
        </li><!-- /.step -->

        <li class="step--alt step--secondary">
          <div class="step__inner">
            <div class="step__count">
              <img src="assets/images/svg/5.svg" alt="" width="29" height="56">
            </div><!-- /.step__count -->

            <div class="step__icon">
              <i class="ico-balloon">
                <img src="assets/images/svg/calendar-alt.svg" alt="" width="75" height="66">
              </i>
            </div><!-- /.step__icon -->
          </div><!-- /.step__inner -->

          <div class="step__content">
            <p v-html="$t('product.step_5')" />
          </div><!-- /.step__inner -->
        </li><!-- /.step -->
      </ul><!-- /.steps-/-alt -->
    </div>
  </section>

  <section class="section section-about section-about--secondary section--solid">
    <div class="container">
      <div class="section__body">
        <div class="section__content">
          <div class="tabs tabs--alt">
            <ul class="nav nav-tabs nav-tabs--alt" id="tab-details" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active" id="details-tab" data-bs-toggle="tab" data-bs-target="#details" type="button" role="tab" aria-controls="details" aria-selected="true">{{ $t('product.tab_details') }}</button>
              </li>

              <li class="nav-item" role="presentation">
                <button class="nav-link" id="questions-tab" data-bs-toggle="tab" data-bs-target="#questions" type="button" role="tab" aria-controls="questions" aria-selected="false">{{ $t('product.tab_faq') }}</button>
              </li>

              <li class="nav-item" role="presentation">
                <button class="nav-link" id="shipping-tab" data-bs-toggle="tab" data-bs-target="#shipping" type="button" role="tab" aria-controls="shipping" aria-selected="false">{{ $t('product.tab_shipping') }}</button>
              </li>
            </ul>

            <div class="tab-content tab-content--alt" id="tabContent">
              <div class="tab-pane fade show active" id="details" role="tabpanel" aria-labelledby="details-tab">
                <div class="mb-5" v-html="product.description" />
              </div>

              <div class="tab-pane fade" id="questions" role="tabpanel" aria-labelledby="questions-tab">
                <div class="list-details tabs__list-details" v-html="$t('product.tab_faq.content')">
                </div>
                <div class="list-details tabs__list-details" v-if="product.customFields?.custom_productfields_faqs" v-html="product.customFields?.custom_productfields_faqs">
                </div>

                <div class="tab-content__inner">
                  <a href="/faq" class="link-more">
                    <span v-html="$t('product.tab_moreFaq')"></span>
                  </a>
                </div><!-- /.tab-content__inner -->
              </div>

              <div class="tab-pane fade" id="shipping" role="tabpanel" aria-labelledby="shipping-tab">
                <div class="list-details tabs__list-details">
                  <div v-html="$t('product.shippingInformation')"></div>
                </div>
              </div>
            </div>
          </div>
        </div><!-- /.section__content -->
      </div><!-- /.section__body -->
    </div><!-- /.container -->
  </section>

  <section class="section section-about">
    <div class="container">
      <header class="section__head"><h2 class="text-center mb-5">{{$t('etrust.product.headline')}}</h2></header>
      <!-- Begin eTrusted widget tag -->
      <!--etrusted-widget data-etrusted-widget-id="wdg-d49d80f7-9f03-44a8-8ad2-567433a91751" :data-sku="product.productNumber"></etrusted-widget-->
      <!--etrusted-widget data-etrusted-widget-id="wdg-71b8438e-6e4f-40ed-8c40-2eeb5479af79"></etrusted-widget-->
      <etrusted-widget data-etrusted-widget-id="wdg-6bab3099-2625-4c82-95e5-5833dd16e46a"></etrusted-widget>
      <!-- End eTrusted widget tag -->
    </div>
  </section>

  <ProductsSocialMedia></ProductsSocialMedia>
</template>